import clsx from 'clsx';
import Link from 'next/link';
import type { FC } from 'react';
import { Image } from '@/components/raven/Image';
import Plus from '@themaven-net/raven-assets/Plus';
import { decodeEntities } from '@/utils/post-helpers';
import Checkmark from '@themaven-net/raven-assets/Checkmark';
import { type RavenIcon, getIconComponent } from '@/utils/get-icons';
import { LabelTextContentWrapper } from '@/components/raven/ui/Label/LabelTextContentWrapper';

import styles from './styles.module.css';

type LabelVariant = 'filled' | 'informational' | 'outlined' | 'recipe';

export type CommonLabelProps = {
	className?: string;
	following?: boolean;
	icon?: RavenIcon;
	media?: {
		alt: string;
		caption: string;
		id: string;
		url: string;
	};
	text: string;
	title?: string;
};

type NonLinkLabelProps = {
	href?: never;
	sponsorLink?: never;
	variant?: LabelVariant;
} & CommonLabelProps;

type LinkLabelProps = {
	href: string;
	sponsorLink?: never;
	variant?: Extract<LabelVariant, 'filled' | 'outlined'>;
} & CommonLabelProps;

export type SponsorLabelProps = {
	href?: never;
	sponsorLink?: string;
} & CommonLabelProps;

export type LabelProps = {
	dark?: boolean;
	variant?: LabelVariant;
} & (LinkLabelProps | NonLinkLabelProps | SponsorLabelProps);

const isLinkLabel = (href: LinkLabelProps['href'], variant?: LabelVariant) => {
	return href && (!variant || variant === 'filled' || variant === 'outlined');
};

export const Label: FC<LabelProps> = (props) => {
	const { className, following, icon, media, text, title, variant } = props;
	const { href } = props as LinkLabelProps;
	const { sponsorLink } = props as SponsorLabelProps;

	const isLink = isLinkLabel(href, variant);
	const isFollowing = following !== undefined;
	const decodedTitle = decodeEntities(title);
	const decodedText = decodeEntities(text);

	const cx = clsx(
		styles.label,
		className,
		'label',
		variant && styles[variant],
		{
			[styles.follow]: isFollowing,
			[styles.isDefault]: !variant,
			[styles.isLinked]: href && !sponsorLink,
		},
	);

	const textContent = isFollowing ? (
		<div>
			<div>{decodedText}</div>
			<div className={styles.followCall}>
				{following ? (
					<>
						Following <Checkmark aria-label="checkmark-icon" />
					</>
				) : (
					<>
						Follow
						<Plus aria-label="plus-icon" />
					</>
				)}
			</div>
		</div>
	) : (
		<LabelTextContentWrapper
			sponsorLink={sponsorLink}
			text={decodedText}
			title={decodedTitle}
		/>
	);

	const labelBlock = (
		<>
			{media && media.url && (
				<Image
					className={styles.media}
					{...media}
					alt={media.alt || ''}
					height={1}
					sizes="auto"
					src={media.url}
					width={1}
				/>
			)}
			{icon && getIconComponent(icon, 20)}
			{textContent}
		</>
	);

	if (isFollowing) {
		return <div className={cx}>{labelBlock}</div>;
	}

	return isLink && !sponsorLink ? (
		<Link className={cx} href={href}>
			{labelBlock}
		</Link>
	) : (
		<span className={cx}>{labelBlock}</span>
	);
};
